import { Component, ChangeDetectionStrategy, inject, signal, OnInit, computed, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { EditorModule } from 'primeng/editor';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { finalize } from 'rxjs';
import { registerQuillMention, type QuillEditorInstance } from '../../core/quill-mention-setup';
import { buildMentionHtml, hydrateMentionsInHtml } from '../../core/quill-mention-html';
import { CommunicationTemplateService } from '../../services/communication-template.service';
import {
  CommunicationType,
  CommunicationTypeOptions,
  CommunicationVarDTO,
  CreateCommunicationTemplateRequest,
  ReadFileResponse
} from '../../core/models';

interface MentionListItem {
  id: string;
  value: string;
  label?: string;
}

interface PendingTemplateAttachment {
  key: string;
  file: File;
}

@Component({
  selector: 'app-communication-template-form',
  templateUrl: './communication-template-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host ::ng-deep .sale-form-card.p-card .p-card-header { padding: 0; }
      :host ::ng-deep .p-editor-container { border-radius: 0.5rem; overflow: visible; }
      :host ::ng-deep .p-editor-toolbar {
        background: rgb(248 250 252);
        border-bottom: 1px solid rgb(226 232 240);
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem;
        row-gap: 0.5rem;
      }
      :host ::ng-deep .p-editor-toolbar .ql-formats {
        margin-right: 0.5rem;
      }
      :host ::ng-deep .p-editor-toolbar .ql-picker-options {
        z-index: 1100;
      }
      :host ::ng-deep .ql-picker.ql-font .ql-picker-label::before,
      :host ::ng-deep .ql-picker.ql-font .ql-picker-item::before { content: 'Sans Serif'; }
      :host ::ng-deep .ql-picker.ql-font .ql-picker-label[data-value='serif']::before,
      :host ::ng-deep .ql-picker.ql-font .ql-picker-item[data-value='serif']::before { content: 'Serif'; }
      :host ::ng-deep .ql-picker.ql-font .ql-picker-label[data-value='monospace']::before,
      :host ::ng-deep .ql-picker.ql-font .ql-picker-item[data-value='monospace']::before { content: 'Monoespaciada'; }
      :host ::ng-deep .ql-picker.ql-header .ql-picker-label::before,
      :host ::ng-deep .ql-picker.ql-header .ql-picker-item::before { content: 'Normal'; }
      :host ::ng-deep .ql-picker.ql-header .ql-picker-label[data-value='1']::before,
      :host ::ng-deep .ql-picker.ql-header .ql-picker-item[data-value='1']::before { content: 'Título 1'; }
      :host ::ng-deep .ql-picker.ql-header .ql-picker-label[data-value='2']::before,
      :host ::ng-deep .ql-picker.ql-header .ql-picker-item[data-value='2']::before { content: 'Título 2'; }
      :host ::ng-deep .ql-picker.ql-header .ql-picker-label[data-value='3']::before,
      :host ::ng-deep .ql-picker.ql-header .ql-picker-item[data-value='3']::before { content: 'Título 3'; }
      :host ::ng-deep .ql-editor {
        white-space: pre-wrap;
        word-wrap: break-word;
        font-size: 1.125rem;
        line-height: 1.65;
      }
      :host ::ng-deep .ql-editor p,
      :host ::ng-deep .ql-editor li {
        font-size: 1.125rem;
        line-height: 1.65;
      }
      :host ::ng-deep .mention, :host ::ng-deep .ql-mention-denotation-char { color: #185fa5; font-weight: 600; }
      :host ::ng-deep .email-preview-frame {
        border: 1px solid rgb(226 232 240);
        border-radius: 0.75rem;
        background: white;
        box-shadow: 0 1px 3px rgb(15 23 42 / 0.08);
        max-width: 100%;
        overflow: hidden;
      }
      :host ::ng-deep .email-preview-header {
        background: rgb(248 250 252);
        border-bottom: 1px solid rgb(226 232 240);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .email-preview-header .email-preview-subject {
        white-space: pre-wrap;
        word-break: break-word;
        overflow-wrap: anywhere;
      }
      :host ::ng-deep .email-preview-body {
        padding: 1.25rem;
        min-height: 12rem;
        max-width: 100%;
        overflow-x: hidden;
        overflow-wrap: anywhere;
        word-break: break-word;
        line-height: 1.65;
        font-size: 1rem;
        color: rgb(15 23 42);
      }
      :host ::ng-deep .email-preview-body p {
        margin: 0;
        padding: 0;
        min-height: 1.5em;
        white-space: pre-wrap;
        word-break: break-word;
        overflow-wrap: anywhere;
      }
      :host ::ng-deep .email-preview-body ol,
      :host ::ng-deep .email-preview-body ul {
        margin: 0;
        padding: 0 0 0 1.5em;
        white-space: pre-wrap;
        word-break: break-word;
        overflow-wrap: anywhere;
      }
      :host ::ng-deep .email-preview-body ul { list-style-type: disc; }
      :host ::ng-deep .email-preview-body ol { list-style-type: decimal; }
      :host ::ng-deep .email-preview-body li[data-list='bullet'] { list-style-type: disc; }
      :host ::ng-deep .email-preview-body li[data-list='ordered'] { list-style-type: decimal; }
      :host ::ng-deep .email-preview-body li {
        margin: 0;
        padding: 0;
      }
      :host ::ng-deep .email-preview-body .ql-ui {
        display: none;
      }
      :host ::ng-deep .email-preview-body .ql-align-center { text-align: center; }
      :host ::ng-deep .email-preview-body .ql-align-right { text-align: right; }
      :host ::ng-deep .email-preview-body .ql-align-justify { text-align: justify; }
      :host ::ng-deep .message-editor-shell.html-mode .ql-container { display: none; }
      :host ::ng-deep .p-editor-toolbar .pento-html-source-formats {
        margin-left: auto;
        margin-right: 0;
      }
      :host ::ng-deep .pento-html-source-toggle {
        width: 28px;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 13px;
        font-weight: 600;
        line-height: 1;
      }
      :host ::ng-deep .pento-html-source-toggle.ql-active { color: #06c; }
      :host ::ng-deep .editor-html-source {
        width: 100%;
        height: 450px;
        min-height: 450px;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 0.8125rem;
        line-height: 1.5;
        border: none;
        border-top: 1px solid rgb(226 232 240);
        padding: 0.75rem 0.9375rem;
        background: white;
        color: rgb(15 23 42);
        resize: vertical;
        box-sizing: border-box;
        outline: none;
      }
      :host ::ng-deep .message-composer-body {
        padding: 1rem;
        background: white;
      }
      :host ::ng-deep .message-composer-inner {
        position: relative;
      }
      :host ::ng-deep .message-editor-shell--with-vars {
        padding-right: calc(33.333333% + 0.75rem);
        border: 1px solid rgb(226 232 240);
        border-right: none;
        overflow: hidden;
        background: white;
      }
      :host ::ng-deep .message-vars-panel {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        display: flex;
        flex-direction: column;
        width: 33.333333%;
        border: 1px solid rgb(226 232 240);
        border-left: none;
        background: rgb(248 250 252);
        overflow: hidden;
      }
      :host ::ng-deep .message-vars-header {
        flex-shrink: 0;
        border-bottom: 1px solid rgb(226 232 240);
        padding: 0.75rem 1rem;
        background: rgb(241 245 249 / 0.8);
      }
      :host ::ng-deep .message-vars-list {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        padding: 0.75rem;
      }
      :host ::ng-deep .message-var-item {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.125rem;
        width: 100%;
        text-align: left;
        border: 1px solid rgb(226 232 240);
        border-radius: 0.5rem;
        background: white;
        padding: 0.5rem 0.625rem;
        margin-bottom: 0.5rem;
        cursor: pointer;
        transition: background-color 0.15s ease, border-color 0.15s ease;
      }
      :host ::ng-deep .message-var-item:hover {
        background: rgb(240 249 255);
        border-color: rgb(125 211 252);
      }
      :host ::ng-deep .message-editor-loading {
        min-height: 450px;
      }
      @media (max-width: 767px) {
        :host ::ng-deep .message-editor-shell--with-vars {
          padding-right: 0;
          margin-bottom: 0.75rem;
          border-right: 1px solid rgb(226 232 240);
        }
        :host ::ng-deep .message-vars-panel {
          position: static;
          width: 100%;
          margin-top: 0;
          max-height: 12rem;
          border-left: 1px solid rgb(226 232 240);
        }
      }
    `
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    FloatLabelModule,
    SelectModule,
    ButtonModule,
    CardModule,
    TooltipModule,
    DrawerModule,
    EditorModule
  ]
})
export class CommunicationTemplateFormComponent implements OnInit {
  @ViewChild('messageBodyHtmlSource') messageBodyHtmlSource?: ElementRef<HTMLTextAreaElement>;

  private fb = inject(FormBuilder);
  private service = inject(CommunicationTemplateService);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private confirmationService = inject(ConfirmationService);

  templateForm: FormGroup;
  loading = signal(false);
  formDataLoading = signal(false);
  templateId = signal<number | null>(null);
  isEditMode = computed(() => this.templateId() != null);
  error = signal<string | null>(null);

  availableVars = signal<CommunicationVarDTO[]>([]);
  mentionReady = signal(false);

  previewDrawerOpen = signal(false);
  previewSubject = signal('');
  previewBody = signal('');
  previewVariables: Record<string, string> = {};

  editorHtmlMode = signal(false);
  editorHtmlSource = '';

  attachments = signal<ReadFileResponse[]>([]);
  attachmentsLoading = signal(false);
  uploadingAttachments = signal(false);
  pendingAttachments = signal<PendingTemplateAttachment[]>([]);
  private pendingAttachmentSeq = 0;

  quillInstance: QuillEditorInstance | null = null;

  readonly typeOptions = CommunicationTypeOptions;
  readonly editorStyle = { height: '450px' };

  quillModules: Record<string, unknown> = {};

  constructor() {
    this.templateForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      type: [null as CommunicationType | null, Validators.required],
      subject: ['', [Validators.required, Validators.maxLength(500)]],
      messageBody: ['', Validators.required]
    });

    this.quillModules = {
      mention: {
        allowedChars: /^[A-Za-z0-9_]*$/,
        mentionDenotationChars: ['@'],
        positioningStrategy: 'fixed',
        mentionContainerClass: 'ql-mention-list-container pento-mention-menu-panel',
        mentionListClass: 'ql-mention-list pento-mention-menu-list',
        listItemClass: 'ql-mention-list-item pento-mention-menu-item',
        source: (searchTerm: string, renderList: (items: MentionListItem[], term: string) => void) => {
          const matches = this.availableVars()
            .filter((v) => {
              const key = (v.varKey ?? '').toLowerCase();
              const label = (v.label ?? '').toLowerCase();
              const q = searchTerm.toLowerCase();
              return key.includes(q) || label.includes(q);
            })
            .map((v) => ({
              id: String(v.id ?? v.varKey),
              value: v.varKey!,
              label: v.label
            }));
          renderList(matches, searchTerm);
        },
        renderItem: (item: MentionListItem) => {
          const row = document.createElement('span');
          const key = document.createElement('strong');
          key.textContent = `@${item.value}`;
          const label = document.createElement('span');
          label.style.color = '#888';
          label.style.fontSize = '12px';
          label.style.marginLeft = '6px';
          label.textContent = item.label ?? item.value;
          row.appendChild(key);
          row.appendChild(label);
          return row;
        },
        renderLoading: () => {
          const el = document.createElement('span');
          el.textContent = 'Buscando...';
          return el;
        }
      }
    };
  }

  ngOnInit(): void {
    void registerQuillMention().then(() => this.mentionReady.set(true));

    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam && idParam !== 'nuevo') {
      const id = Number(idParam);
      if (!Number.isNaN(id)) {
        this.templateId.set(id);
        this.loadTemplate(id);
      }
    }
    this.templateForm.controls['type'].valueChanges.subscribe((type: CommunicationType | null) => {
      if (type) {
        this.loadVars(type);
      } else {
        this.availableVars.set([]);
      }
    });
  }

  loadTemplate(id: number): void {
    this.formDataLoading.set(true);
    this.service.getById(id)
      .pipe(finalize(() => this.formDataLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res.ok && res.body) {
            const t = res.body;
            this.templateForm.patchValue({
              name: t.name,
              type: t.type,
              subject: t.subject,
              messageBody: hydrateMentionsInHtml(t.messageBody ?? '')
            });
            this.loadVars(t.type as CommunicationType);
            this.loadAttachments(id);
            this.scheduleEditorContentSync();
          } else {
            this.error.set(res.message || 'No se pudo cargar la plantilla');
          }
        },
        error: () => this.error.set('Error al cargar la plantilla')
      });
  }

  loadVars(type: CommunicationType | null | undefined): void {
    if (!type) {
      this.availableVars.set([]);
      return;
    }
    this.service.listVars(type).subscribe({
      next: (res) => {
        if (res.ok) {
          this.availableVars.set(res.body ?? []);
          this.initPreviewVariables(res.body ?? []);
        }
      }
    });
  }

  onEditorInit(event: { editor?: QuillEditorInstance }): void {
    this.quillInstance = event.editor ?? null;
    this.scheduleEditorContentSync();
  }

  private scheduleEditorContentSync(): void {
    setTimeout(() => this.syncMessageBodyWithEditor(), 0);
  }

  private syncMessageBodyWithEditor(): void {
    const quill = this.quillInstance;
    if (!quill) {
      return;
    }

    const raw = this.templateForm.controls['messageBody'].value ?? '';
    if (!raw) {
      return;
    }

    const hydrated = hydrateMentionsInHtml(raw);
    if (hydrated !== raw) {
      this.templateForm.patchValue({ messageBody: hydrated }, { emitEvent: false });
    }

    const hasMentionSpans = hydrated.includes('class="mention"');
    const editorHasMentions = !!quill.root.querySelector('span.mention[data-value]');
    if (hasMentionSpans && !editorHasMentions) {
      const delta = quill.clipboard.convert({ html: hydrated });
      quill.setContents(delta, 'silent');
    }
  }

  toggleEditorHtmlMode(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.editorHtmlMode()) {
      this.applyEditorHtmlSource();
      this.editorHtmlMode.set(false);
      return;
    }

    this.syncEditorHtmlSource();
    this.editorHtmlMode.set(true);
  }

  syncEditorHtmlSource(): void {
    if (this.quillInstance) {
      this.editorHtmlSource = this.quillInstance.root.innerHTML;
    } else {
      this.editorHtmlSource = this.templateForm.controls['messageBody'].value ?? '';
    }
    this.templateForm.patchValue({ messageBody: this.editorHtmlSource });
  }

  applyEditorHtmlSource(): void {
    const html = this.editorHtmlSource;
    this.templateForm.patchValue({ messageBody: html });
    if (this.quillInstance) {
      const delta = this.quillInstance.clipboard.convert({ html });
      this.quillInstance.setContents(delta, 'silent');
    }
  }

  onEditorHtmlSourceChange(): void {
    this.templateForm.patchValue({ messageBody: this.editorHtmlSource });
  }

  private insertIntoHtmlSource(text: string): void {
    const textarea = this.messageBodyHtmlSource?.nativeElement;
    if (!textarea) {
      this.editorHtmlSource += text;
      this.onEditorHtmlSourceChange();
      return;
    }

    const start = textarea.selectionStart ?? this.editorHtmlSource.length;
    const end = textarea.selectionEnd ?? start;
    this.editorHtmlSource = `${this.editorHtmlSource.slice(0, start)}${text}${this.editorHtmlSource.slice(end)}`;
    this.onEditorHtmlSourceChange();

    const newPos = start + text.length;
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newPos, newPos);
    });
  }

  insertVariable(varKey: string): void {
    if (this.editorHtmlMode()) {
      this.insertIntoHtmlSource(buildMentionHtml(varKey));
      return;
    }

    const quill = this.quillInstance;
    if (!quill) return;
    const range = quill.getSelection(true);
    const index = range?.index ?? quill.getLength();
    quill.insertEmbed(index, 'mention', {
      id: varKey,
      value: varKey,
      denotationChar: '@'
    }, 'user');
    quill.insertText(index + 1, ' ', 'user');
    quill.setSelection(index + 2, 0);
  }

  openPreviewDrawer(): void {
    if (this.editorHtmlMode()) {
      this.applyEditorHtmlSource();
      this.editorHtmlMode.set(false);
    }
    this.initPreviewVariables(this.availableVars());
    this.previewDrawerOpen.set(true);
    this.refreshPreview();
  }

  closePreviewDrawer(): void {
    this.previewDrawerOpen.set(false);
  }

  onPreviewDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closePreviewDrawer();
  }

  onPreviewVariableChange(): void {
    this.refreshPreview();
  }

  refreshPreview(): void {
    const form = this.templateForm.getRawValue();
    this.previewSubject.set(this.buildLocalPreview(form.subject ?? '', true));
    this.previewBody.set(this.buildLocalPreview(form.messageBody ?? '', false));
  }

  private buildLocalPreview(content: string, plain: boolean): string {
    if (!content) return '';

    const normalized = content.replace(/\uFEFF/g, '');
    if (plain) {
      return this.resolveAtVariablesInText(normalized, true);
    }
    return this.resolvePreviewVariablesInHtml(normalized);
  }

  private getSortedVars(): CommunicationVarDTO[] {
    return [...this.availableVars()]
      .filter((v) => v.varKey)
      .sort((a, b) => (b.varKey!.length - a.varKey!.length));
  }

  private getPreviewValue(varKey: string): string {
    const variable = this.availableVars().find((v) => v.varKey === varKey);
    return this.previewVariables[varKey] ?? variable?.exampleValue ?? '';
  }

  private buildPreviewToken(varKey: string, plain: boolean): string {
    const value = this.getPreviewValue(varKey);
    return plain ? value : this.escapeHtml(value);
  }

  private resolveAtVariablesInText(content: string, plain: boolean): string {
    let resolved = content;
    const tokens = new Map<string, string>();

    this.getSortedVars().forEach((variable, index) => {
      const varKey = variable.varKey!;
      const placeholder = `\uE000${index}\uE001`;
      tokens.set(placeholder, this.buildPreviewToken(varKey, plain));

      const escapedKey = varKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const atRegex = new RegExp(`@${escapedKey}(?![A-Za-z0-9_])`, 'g');
      resolved = resolved.replace(atRegex, placeholder);
    });

    for (const [placeholder, token] of tokens) {
      resolved = resolved.split(placeholder).join(token);
    }
    return resolved;
  }

  private resolvePreviewVariablesInHtml(html: string): string {
    const doc = new DOMParser().parseFromString(`<div id="pento-preview-root">${html}</div>`, 'text/html');
    const root = doc.getElementById('pento-preview-root');
    if (!root) return this.resolveAtVariablesInText(html, false);

    const mentionPlaceholders = new Map<string, string>();

    root.querySelectorAll('span.mention[data-value]').forEach((node, index) => {
      const varKey = node.getAttribute('data-value');
      if (!varKey) return;

      const placeholder = `\uE000M${index}\uE001`;
      mentionPlaceholders.set(placeholder, this.buildPreviewToken(varKey, false));
      node.replaceWith(doc.createTextNode(placeholder));
    });

    let resolved = this.resolveAtVariablesInText(root.innerHTML, false);

    for (const [placeholder, token] of mentionPlaceholders) {
      resolved = resolved.split(placeholder).join(token);
    }
    return resolved;
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  onSubmit(): void {
    if (this.editorHtmlMode()) {
      this.applyEditorHtmlSource();
      this.editorHtmlMode.set(false);
    }

    if (this.templateForm.invalid) {
      this.templateForm.markAllAsTouched();
      this.toastr.warning('Completa los campos requeridos');
      return;
    }

    const payload = this.templateForm.getRawValue() as CreateCommunicationTemplateRequest;
    this.loading.set(true);
    const creating = !this.isEditMode();
    const pending = [...this.pendingAttachments()];

    const request$ = creating
      ? this.service.create(payload)
      : this.service.update(this.templateId()!, payload);

    request$.subscribe({
      next: async (res) => {
        if (!res.ok) {
          this.loading.set(false);
          this.toastr.error(res.message || 'No se pudo guardar');
          return;
        }

        const id = res.body?.id ?? this.templateId();
        if (creating && id) {
          this.templateId.set(id);
          if (pending.length > 0) {
            const uploaded = await this.uploadPendingAttachments(id, pending);
            this.pendingAttachments.set([]);
            if (uploaded < pending.length) {
              this.toastr.warning(
                `Plantilla creada. Se subieron ${uploaded} de ${pending.length} adjuntos.`
              );
            } else {
              this.toastr.success('Plantilla creada');
            }
          } else {
            this.toastr.success('Plantilla creada');
          }
          this.loading.set(false);
          void this.router.navigate(['/comunicacion', id], { replaceUrl: true });
          return;
        }

        this.loading.set(false);
        this.toastr.success('Plantilla actualizada');
      },
      error: (err) => {
        this.loading.set(false);
        this.toastr.error(err.error?.message || 'Error al guardar');
      }
    });
  }

  cancel(): void {
    void this.router.navigate(['/comunicacion']);
  }

  loadAttachments(templateId: number): void {
    this.attachmentsLoading.set(true);
    this.service.listAttachments(templateId)
      .pipe(finalize(() => this.attachmentsLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.ok) {
            this.attachments.set(response.body ?? []);
          } else {
            this.toastr.error(response.message ?? 'Error al cargar adjuntos');
          }
        },
        error: () => this.toastr.error('Error al cargar adjuntos')
      });
  }

  async onAttachmentSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length === 0) {
      return;
    }

    const MAX_SIZE_MB = 10;
    const maxBytes = MAX_SIZE_MB * 1024 * 1024;
    const templateId = this.templateId();

    if (!templateId) {
      const accepted: PendingTemplateAttachment[] = [];
      for (const file of files) {
        if (file.size > maxBytes) {
          this.toastr.error(`${file.name} supera ${MAX_SIZE_MB} MB`);
          continue;
        }
        this.pendingAttachmentSeq += 1;
        accepted.push({ key: `pending-${this.pendingAttachmentSeq}`, file });
      }
      if (accepted.length > 0) {
        this.pendingAttachments.update((list) => [...list, ...accepted]);
      }
      return;
    }

    this.uploadingAttachments.set(true);
    let uploaded = 0;

    try {
      for (const file of files) {
        if (file.size > maxBytes) {
          this.toastr.error(`${file.name} supera ${MAX_SIZE_MB} MB`);
          continue;
        }
        const ok = await this.uploadSingleAttachment(templateId, file);
        if (ok) {
          uploaded += 1;
        }
      }
      if (uploaded > 0) {
        this.toastr.success(uploaded === 1 ? 'Archivo adjuntado correctamente' : `${uploaded} archivos adjuntados`);
        this.loadAttachments(templateId);
      }
    } catch (err: unknown) {
      const message = (err as { error?: { message?: string } })?.error?.message ?? 'Error al subir el archivo';
      this.toastr.error(message);
    } finally {
      this.uploadingAttachments.set(false);
    }
  }

  removePendingAttachment(key: string): void {
    this.pendingAttachments.update((list) => list.filter((item) => item.key !== key));
  }

  openAttachment(file: ReadFileResponse): void {
    if (!file.content) {
      this.toastr.error('No hay URL de descarga para este archivo');
      return;
    }
    window.open(file.content, '_blank', 'noopener');
  }

  confirmDeleteAttachment(file: ReadFileResponse): void {
    if (file.id == null) {
      return;
    }
    const templateId = this.templateId();
    if (!templateId) {
      return;
    }
    this.confirmationService.confirm({
      message: `¿Eliminar el archivo "${file.name ?? 'adjunto'}"?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.service.deleteAttachment(templateId, file.id!).subscribe({
          next: (res) => {
            if (res?.ok !== false) {
              this.attachments.update((list) => list.filter((f) => f.id !== file.id));
              this.toastr.success('Archivo eliminado correctamente');
            } else {
              this.toastr.error(res?.message ?? 'Error al eliminar el archivo');
            }
          },
          error: (err) => this.toastr.error(err.error?.message ?? 'Error al eliminar el archivo')
        });
      }
    });
  }

  formatFileSize(size?: number): string {
    if (size == null || !Number.isFinite(size)) {
      return '-';
    }
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }

  getFileIcon(contentType?: string): string {
    const type = (contentType ?? '').toLowerCase();
    if (type.startsWith('image/')) return 'pi-image';
    if (type === 'application/pdf') return 'pi-file-pdf';
    if (type.includes('spreadsheet') || type.includes('excel')) return 'pi-file-excel';
    if (type.includes('word') || type.includes('msword')) return 'pi-file-word';
    return 'pi-file';
  }

  private async uploadPendingAttachments(
    templateId: number,
    pending: PendingTemplateAttachment[]
  ): Promise<number> {
    let uploaded = 0;
    for (const item of pending) {
      const ok = await this.uploadSingleAttachment(templateId, item.file);
      if (ok) {
        uploaded += 1;
      }
    }
    return uploaded;
  }

  private async uploadSingleAttachment(templateId: number, file: File): Promise<boolean> {
    try {
      const contentBase64 = await this.readFileAsBase64(file);
      const response = await new Promise<{ ok?: boolean; message?: string; body?: ReadFileResponse }>((resolve, reject) => {
        this.service.attachFile(templateId, {
          name: file.name,
          contentType: file.type || 'application/octet-stream',
          contentBase64
        }).subscribe({ next: resolve, error: reject });
      });
      if (response.ok && response.body) {
        return true;
      }
      this.toastr.error(response.message ?? `Error al subir ${file.name}`);
      return false;
    } catch (err: unknown) {
      const message = (err as { error?: { message?: string } })?.error?.message ?? `Error al subir ${file.name}`;
      this.toastr.error(message);
      return false;
    }
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64 ?? '');
      };
      reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo'));
      reader.readAsDataURL(file);
    });
  }

  private initPreviewVariables(vars: CommunicationVarDTO[]): void {
    vars.forEach((v) => {
      if (v.varKey && this.previewVariables[v.varKey] === undefined) {
        this.previewVariables[v.varKey] = v.exampleValue ?? '';
      }
    });
  }
}
